import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SearchService from '@/pages/index/services/search.service';
import Header from '../Header';
import Footer from '@/shared/components/footer/footer';
import './styles/searchResult.css';
import SearchCards from '@/shared/components/SearchCards/SearchCards';
import '../../../../../shared/components/SearchCards/styles/SeachCards.css';
import MobileFooter from '@/shared/components/MobileFooter/MobileFooter';
import NavigationMobileMenu from '@/shared/components/NavigationMobileMenu/NavigationMobileMenu';
import FlotingCart from '../floatingcart';
import { useCart } from '../../../../../contexts/CartContext';
import { useWishlist } from '../../../../wishlist/WishlistContext';
import VariantSheetDesktop from '@/shared/components/VariantSheetDesktop/VariantSheetDesktop';
import VariantOptionsModal from '@/shared/components/VariantOptionModal/VariantOptionsModal';
import VariantOptionModalMobile from '@/shared/components/VariantOptionModal/VariantOptionModalMobile';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory] = useState<string>('All Categories');
  const [isMobile, setIsMobile] = useState(false);
  const [modalProduct, setModalProduct] = useState<any>(null);
  const productsApi = new SearchService();
  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);

  // Helper function to convert variants for display
  const convertVariantsForDisplay = (product: any) => {
    if (!product.variants || product.variants.length === 0) {
      return [{
        id: '',
        size: product.name || product.title,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        rollLength: 1,
        attributes: {},
        variantImage: undefined,
        variantImages: [],
      }];
    }
    return product.variants.map((variant: any) => {
      let variantDisplayName = variant.variantTitle || variant.sku;
      if (variant.attributes) {
        const attributeValues = Object.values(variant.attributes);
        if (attributeValues.length > 0) {
          variantDisplayName = attributeValues[0] as string;
        }
      }
      const baseVariant = {
        id: variant.sku || variant._id,
        size: variantDisplayName,
        price: variant.price || product.price,
        compareAtPrice: variant.compareAtPrice,
        pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
        rollLength: variant.weight || 90,
        attributes: variant.attributes || {},
        variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
        variantImages: variant.variantImages || [],
        ...variant
      };
      return baseVariant;
    });
  };

  // Helper function to detect variant hierarchy
  const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
    if (!variants || variants.length === 0) return undefined;
    
    const firstVariant = variants[0];
    if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
      return undefined;
    }
    
    if (firstVariant.variantLevels) {
      return Object.keys(firstVariant.variantLevels).map((key: string) => {
        const match = key.match(/level(\d+)/i);
        if (match) {
          return firstVariant.variantLevels[key];
        }
        return firstVariant.variantLevels[key];
      });
    }
    
    const attributeKeys = Object.keys(firstVariant.attributes);
    if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
      return attributeKeys;
    }
    
    return undefined;
  };

  // Memoize modal data
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: modalProduct.variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  const getTruncatedTitle = (title?: string, maxWords: number = 6) => {
    const safeTitle = (title || '').trim() || 'Item';
    const words = safeTitle.split(/\s+/);
    if (words.length <= maxWords) return safeTitle;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const formatPrice = (value: unknown): string => {
    const numericPrice = Number(
      typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
    );

    if (Number.isFinite(numericPrice)) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(numericPrice);
    }

    return String(value ?? '');
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile/tablet, hide keyboard when user scrolls the page/results
  useEffect(() => {
    if (!isMobile) return;

    const blurActiveInput = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) return;

      const tag = activeElement.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        activeElement.blur();
      }
    };

    const handleScrollOrMove = () => {
      blurActiveInput();
    };

    window.addEventListener('scroll', handleScrollOrMove, { passive: true, capture: true });
    window.addEventListener('touchmove', handleScrollOrMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollOrMove, { capture: true } as any);
      window.removeEventListener('touchmove', handleScrollOrMove);
    };
  }, [isMobile]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearchTerm(query);

    if (query) {
      fetchSearchResults(query);
    } else {
      setSearchResults([]);
    }
  }, [location.search]);

  const fetchSearchResults = async (query: string) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(location.search);

      // Enhanced search options - SKU search is ALWAYS enabled
      const searchOptions = {
        searchInTitle: params.get('searchInTitle') !== 'false',
        searchInDescription: params.get('searchInDescription') !== 'false',
        searchInCategories: params.get('searchInCategories') !== 'false',
        searchInTags: params.get('searchInTags') !== 'false',
        searchInSku: true, // ALWAYS SEARCH IN SKU
        searchInSpecifications: params.get('searchInSpecifications') !== 'false',
        searchInBrand: params.get('searchInBrand') !== 'false',
      };

      console.log('🔍 Search Query:', query);
      console.log('🔧 Search Options:', searchOptions);

      const response = await productsApi.SearchProducts(
        query,
        parseInt(params.get('page') || '1'),
        20,
        searchOptions
      );

      console.log('✅ API Response:', response);
      console.log('📊 Results Count:', response.data?.length || 0);
      
      if (response.data && response.data.length > 0) {
        console.log('📦 First Product SKU:', response.data[0].sku);
        console.log('📦 First Product Title:', response.data[0].title);
      }

      setSearchResults(response.data || []);
    } catch (error) {
      console.error('❌ Search Error:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = (product: any) => {
    try {
      let imgSrc = product.imageUrl;
      if (!imgSrc && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
        imgSrc = product.images[0].url;
      }
      const cartProduct = {
        id: product._id,
        _id: product._id,
        title: product.title || product.name || 'Unnamed Product',
        name: product.title || product.name || 'Unnamed Product',
        slug: product.slug,
        price: product.price || 0,
        imageUrl: imgSrc || '',
        description: product.description || ''
      };
      addToCart(cartProduct, 1);
      if (!isMobile) {
        const title = cartProduct.title || cartProduct.name;
        toast.success(`${getTruncatedTitle(title)} added to cart!`, {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      if (!isMobile) {
        toast.error('Failed to add item to cart', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    }
  };

  const handleWishlistToggle = (product: any) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return;
    lastWishlistActionRef.current = now;

    const isWishlisted = isItemInWishlist(product._id);
    
    if (isWishlisted) {
      removeFromWishlist(product._id);
      if (isMobile) {
        toast.info('Removed from wishlist', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    } else {
      let primaryImage = product.images?.[0]?.url;
      const productForWishlist = {
        id: product._id,
        _id: product._id,
        title: product.title || product.name || 'Unnamed Product',
        slug: product.slug,
        description: product.description || '',
        price: product.price || 0,
        compareAtPrice: product.compareAtPrice ?? null,
        quantity: product.quantity ?? 0,
        images: primaryImage ? [primaryImage] : [],
        isActive: product.isActive,
        isFeatured: product.isFeatured ?? false,
        hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
        categories: (product.categories || []).map((c: any) => c.name),
        tags: product.tags || []
      };

      addToWishlist(productForWishlist);
      if (isMobile) {
        toast.success('Added to wishlist', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    }
  };

  const filteredResults = selectedCategory === 'All Categories'
    ? searchResults
    : searchResults.filter(product =>
      product.categories?.some((c: any) => c.name === selectedCategory)
    );

  return (
    <div className="search-results-page">
      <Header />

      <main className="search-results-main">
        <div className="results-section">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredResults.length > 0 ? (
            <>
              <h2 className="results-header">
                Search results for "{searchTerm}"
              </h2>
              <div className="products-grid">
                {filteredResults.map((product) => {
                  const term = (searchTerm || '').toLowerCase();
                  const matchedVariant = (product.variants || []).find((v: any) =>
                    (v.variantTitle && v.variantTitle.toLowerCase().includes(term)) ||
                    (v.variantDescription && v.variantDescription.toLowerCase().includes(term))
                  );
                  const baseTitle = product.name || product.title;
                  const displayTitle = matchedVariant?.variantTitle ? `${baseTitle} — ${matchedVariant.variantTitle}` : baseTitle;
                  const imageUrl = matchedVariant?.variantImage || product.images?.[0]?.url;
                  return (
                    <SearchCards
                      key={product._id}
                      id={product._id}
                      title={displayTitle}
                      price={formatPrice(product.price)}
                      image={imageUrl || ''}
                      slug={product.slug || String(product._id)}
                      onMoreInfo={() => navigate(`/product/${product.slug || product._id}`)}
                      onAddToCart={() => handleAddToCart(product)}
                      onAddToWishlist={() => handleWishlistToggle(product)}
                      isWishlisted={isItemInWishlist(product._id)}
                      quantity={product.quantity}
                      product={product}
                      onViewOptions={(prod) => setModalProduct(prod)}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <svg
                className="empty-state-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="empty-state-title">Find What You're Looking For</h3>
              <p className="empty-state-description">
                Search for products, categories, or brands to discover amazing deals and offers
              </p>
              <div className="empty-state-action">
                <button
                  onClick={() => navigate('/')}
                  className="back-home-button"
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Variant Options Modal - Rendered outside the grid */}
      {modalProduct && modalData && (
        isMobile ? (
          <VariantOptionModalMobile
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            product={modalProduct}
            variants={modalData.variants}
            hierarchicalVariants={modalData.hierarchicalVariants}
            variantHierarchy={modalData.variantHierarchy}
          />
        ) : (
          <VariantOptionsModal
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            title={modalProduct ? (modalProduct.title || 'Product Options') : ''}
          >
            <VariantSheetDesktop
              product={modalProduct}
              variants={modalData.variants}
              hierarchicalVariants={modalData.hierarchicalVariants}
              variantHierarchy={modalData.variantHierarchy}
            />
          </VariantOptionsModal>
        )
      )}

      {!isMobile && <Footer/>}
      {isMobile && <MobileFooter/>}
      <FlotingCart/>
      {isMobile && <NavigationMobileMenu/>}
    </div>
  );
};

export default SearchResults;